import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import { stripePublishableKey } from '../../config';
import {
  AddCardPaymentProps,
  CardFormProps,
  StripeSetupIntent,
} from '../../types/stripePayments';
import {
  createSetupIntentForCustomer,
  updateStripeCustomer,
} from '../../middleware/registration_service/createRequest';

const stripePromise = loadStripe(stripePublishableKey);

// ── Inner form — must be a child of <Elements> ────────────────────────────────
const CardForm: React.FC<CardFormProps> = ({
  customerId,
  clientSecret,
  isUpdate,
  onCancel,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getAccessTokenSilently } = useAuth0();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setSubmitting(true);
    setApiError(null);
    setSuccess(false);

    try {
      const { setupIntent, error } = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: { card: cardElement } }
      );

      if (error) {
        setApiError(
          error.message ?? 'Failed to confirm card. Please try again.'
        );
        return;
      }

      if (setupIntent?.payment_method) {
        const pmId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method.id;

        // Set the new card as the customer's default payment method
        const token = await getAccessTokenSilently();
        await updateStripeCustomer(
          customerId,
          {
            invoiceSettings: { defaultPaymentMethod: pmId },
          },
          token
        );
      }

      setSuccess(true);
      cardElement.clear();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setApiError(
        err?.message ?? 'Failed to add payment card. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isUpdate
            ? 'New payment card added successfully.'
            : 'Payment card added successfully.'}
        </Alert>
      )}
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      {/* Stripe-hosted card field — card data never enters React state */}
      <Box
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&:focus-within': { borderColor: 'primary.main' },
        }}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#9e2146' },
            },
            hidePostalCode: true,
          }}
        />
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={submitting || !stripe}
        sx={{ mt: 1 }}
        startIcon={
          submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <CreditCardIcon />
          )
        }
      >
        {submitting ? 'Processing…' : isUpdate ? 'Add New Card' : 'Add Card'}
      </Button>

      {onCancel && (
        <Button
          variant="text"
          color="inherit"
          fullWidth
          sx={{ mt: 1 }}
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
      )}
    </form>
  );
};

// ── Outer wrapper — fetches SetupIntent client_secret then mounts Elements ────
const AddCardPayment: React.FC<AddCardPaymentProps> = ({
  customerId,
  isUpdate = false,
  onCancel,
  onSuccess,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!customerId) return;
    setClientSecret(null);
    setLoadError(null);
    getAccessTokenSilently()
      .then((token) => createSetupIntentForCustomer(customerId, token))
      .then((response) => {
        const setupIntent: StripeSetupIntent = response.data;
        const clientSecret = setupIntent?.clientSecret;
        if (clientSecret) {
          setClientSecret(clientSecret);
        } else {
          setLoadError('Unable to initialise payment form. Please try again.');
        }
      })
      .catch(() =>
        setLoadError('Unable to initialise payment form. Please try again.')
      );
  }, [customerId, getAccessTokenSilently]);

  return (
    <Box>
      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CreditCardIcon color="action" />
        <Typography variant="subtitle1" fontWeight={600}>
          {isUpdate ? 'Add New Payment Card' : 'Add Payment Card'}
        </Typography>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {!clientSecret && !loadError && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Initialising payment form…
          </Typography>
        </Box>
      )}

      {clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CardForm
            customerId={customerId}
            clientSecret={clientSecret}
            isUpdate={isUpdate}
            onCancel={onCancel}
            onSuccess={onSuccess}
          />
        </Elements>
      )}
    </Box>
  );
};

export default AddCardPayment;
